export type RelativeDirection = 'ago' | 'from_now';

/**
 * Computes an absolute ISO date string from a relative date specification.
 * @param relativeValue - Number of days to offset
 * @param _relativeUnit - Time unit (currently only 'days' supported, reserved for future expansion)
 * @param relativeDirection - Direction of offset: 'ago' for past, 'from_now' for future
 * @returns ISO 8601 date string
 */
export function computeAbsoluteDateFromRelative(
  relativeValue: number,
  _relativeUnit: 'days',
  relativeDirection: RelativeDirection
): string {
  const now = new Date();
  const multiplier = relativeDirection === 'ago' ? -1 : 1;
  const offsetMs = relativeValue * 24 * 60 * 60 * 1000 * multiplier;
  const targetDate = new Date(now.getTime() + offsetMs);
  return targetDate.toISOString();
}

interface FilterConditionLike {
  dateMode?: 'absolute' | 'relative';
  relativeValue?: number;
  relativeUnit?: 'days';
  relativeDirection?: RelativeDirection;
  relativeValueEnd?: number;
  relativeDirectionEnd?: RelativeDirection;
  operator?: string;
  value?: string;
  valueEnd?: string;
  [key: string]: unknown;
}

interface FilterGroupLike {
  conditions: FilterConditionLike[];
  [key: string]: unknown;
}

/**
 * Re-computes absolute date values for any conditions using relative date mode.
 * Used when loading saved filters so that "7 days ago" is computed from NOW,
 * not from when the filter was originally saved.
 */
export function recomputeRelativeDates<T extends FilterGroupLike>(groups: T[]): T[] {
  return groups.map((group) => ({
    ...group,
    conditions: group.conditions.map((c) => {
      if (c.dateMode === 'relative' && c.relativeValue != null && c.relativeDirection) {
        const unit = c.relativeUnit || 'days';
        const computedValue = computeAbsoluteDateFromRelative(
          c.relativeValue,
          unit,
          c.relativeDirection
        );

        if (c.operator === 'between' && c.relativeValueEnd != null) {
          const directionEnd = c.relativeDirectionEnd || c.relativeDirection;
          const computedEnd = computeAbsoluteDateFromRelative(
            c.relativeValueEnd,
            unit,
            directionEnd
          );
          return { ...c, value: computedValue, valueEnd: computedEnd, relativeUnit: unit };
        }

        return { ...c, value: computedValue, relativeUnit: unit };
      }
      return c;
    }),
  }));
}
